import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';

interface IdVerificationRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  id_status: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  id_image_url: string | null;
  id_submitted_at: string | null;
  id_reviewed_at: string | null;
  id_rejection_reason: string | null;
  signed_url: string | null;
}

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

/**
 * Admin queue for reviewing customer ID uploads. Lives at /admin/customers
 * under an "ID Verifications" tab so admin can toggle without leaving the
 * customers context.
 */
export function AdminIdVerifications() {
  const [rows, setRows] = useState<IdVerificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    const q = filter === 'all' ? '' : `?status=${filter}`;
    apiFetch<IdVerificationRow[]>(`/api/admin/id-verifications${q}`)
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [filter]);

  async function review(userId: string, approve: boolean) {
    const reason = approve
      ? undefined
      : window.prompt(
          'Reason for rejection? (Shown to the customer.)',
          'Image is too blurry to read.'
        );
    if (!approve && (reason == null || reason.trim() === '')) return;
    setWorking(userId);
    setError(null);
    try {
      await apiFetch(`/api/admin/id-verifications/${userId}/review`, {
        method: 'POST',
        body: JSON.stringify({ approve, reason: reason ?? undefined })
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setWorking(null);
    }
  }

  const counts = rows.reduce(
    (acc, r) => {
      acc[r.id_status] = (acc[r.id_status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <>
      <div className="a-tabs">
        <Link to="/admin/customers" className="a-tab">
          Customers
        </Link>
        <button type="button" className="a-tab active">
          ID Verifications
        </button>
      </div>

      <div className="admin-page-head">
        <div>
          <h1>ID Verifications</h1>
          <div className="sub">
            Review customer photo IDs. Approve to unlock first-order shipping; reject
            with a reason to ask for a fresh upload.
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-card">
        <div className="a-toolbar">
          {(['pending', 'approved', 'rejected', 'all'] as Filter[]).map((k) => (
            <button
              key={k}
              className={`a-filter${filter === k ? ' active' : ''}`}
              onClick={() => setFilter(k)}
            >
              {k.charAt(0).toUpperCase() + k.slice(1)}
              {k !== 'all' && counts[k] != null && (
                <span style={{ opacity: 0.7, marginLeft: 4 }}>{counts[k]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">Nothing here</div>
            <div className="p">
              {filter === 'pending'
                ? 'No IDs awaiting review — great.'
                : `No customers with status "${filter}".`}
            </div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th style={{ width: 88 }}>ID</th>
                  <th>Customer</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 130 }}>Submitted</th>
                  <th style={{ width: 240 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.signed_url ? (
                        <a
                          href={r.signed_url}
                          target="_blank"
                          rel="noopener"
                          title="Open full image"
                        >
                          <img
                            src={r.signed_url}
                            alt={`${r.email} ID`}
                            style={{
                              width: 64,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid var(--a-line)'
                            }}
                          />
                        </a>
                      ) : (
                        <span style={{ color: 'var(--a-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                      </div>
                      <div className="id">{r.email}</div>
                      {r.id_rejection_reason && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 11,
                            color: 'var(--a-danger)'
                          }}
                        >
                          {r.id_rejection_reason}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          r.id_status === 'approved'
                            ? 'badge-good'
                            : r.id_status === 'rejected'
                              ? 'badge-danger'
                              : r.id_status === 'pending'
                                ? 'badge-warn'
                                : 'badge-neutral'
                        }`}
                      >
                        {r.id_status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.id_submitted_at
                        ? new Date(r.id_submitted_at).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                    <td>
                      {r.id_status === 'approved' ? (
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={working === r.id}
                          onClick={() => void review(r.id, false)}
                          style={{ color: 'var(--a-danger)' }}
                        >
                          Revoke
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={working === r.id}
                            onClick={() => void review(r.id, true)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={working === r.id}
                            onClick={() => void review(r.id, false)}
                            style={{ color: 'var(--a-danger)' }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
